import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { adminApi, useAdminCall, useAdminRefundCall } from '@/entities/admin'
import { Button } from '@/shared/ui'
import { ApiError } from '@/shared/api/client'
import type { Highlight } from '@/shared/api/types'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { DeleteCallButton } from '../shared/AdminCallsTable'
import { ElevenLabsCost } from '../shared/ElevenLabsCost'
import { aggregateCallCosts, formatCallCostUsd } from '../shared/adminCosts'
import {
  callKindLabel,
  callStatusLabel,
  callStatusTone,
  formatAdminDate,
  formatDurationMs,
} from '../shared/format'
import styles from '../AdminPages.module.css'

export function AdminCallPage() {
  const { id } = useParams<{ id: string }>()
  const parsedId = Number(id)
  const call = useAdminCall(Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null)
  const navigate = useNavigate()

  const highlightsByTurn = useMemo(() => {
    const result = new Map<number, Highlight[]>()
    for (const highlight of call.data?.highlights ?? []) {
      const current = result.get(highlight.turnIndex)
      if (current) current.push(highlight)
      else result.set(highlight.turnIndex, [highlight])
    }
    return result
  }, [call.data?.highlights])

  if (call.isPending) {
    return (
      <>
        <PageHeader title="Звонок" />
        <AdminRemoteState kind="loading" />
      </>
    )
  }

  if (call.isError || !call.data) {
    const missing = call.error instanceof ApiError && call.error.status === 404
    return (
      <>
        <PageHeader title={missing ? 'Звонок не найден' : 'Звонок недоступен'} />
        <AdminRemoteState
          kind={missing ? 'empty' : 'error'}
          message={missing ? 'Проверьте номер звонка в адресе.' : 'Не удалось загрузить звонок и стенограмму.'}
          onRetry={missing ? undefined : () => void call.refetch()}
        />
      </>
    )
  }

  const data = call.data
  const info = data.call
  const costs = aggregateCallCosts(data.turns, data.elevenLabsUsdPer1kCredits)
  const ttsUsd = costs.completeness.ttsUsd ? costs.ttsUsd : undefined
  const ttsSourceComplete =
    costs.completeness.ttsCredits &&
    (costs.completeness.ttsUsd || costs.elevenLabsUsdPer1kCredits !== undefined)
  const tone = callStatusTone(info.status)
  const statusClass = [
    styles.status,
    tone === 'good' && styles.statusGood,
    tone === 'danger' && styles.statusDanger,
    tone === 'warn' && styles.statusWarn,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <Link className={styles.backLink} to="/admin/calls">
        ← Ко всем звонкам
      </Link>
      <div className={styles.headerRow}>
        <PageHeader
          title={`Звонок № ${info.id}`}
          description={`${data.companyName} · ${formatAdminDate(info.createdAt)}`}
        />
        <div className={styles.headerActions}>
          <Link className={styles.link} to={`/admin/companies/${data.companyId}`}>
            Открыть компанию
          </Link>
          <span className={statusClass}>{callStatusLabel(info.status)}</span>
          <DeleteCallButton call={info} onDeleted={() => navigate('/admin/calls')} />
        </div>
      </div>

      <section className={styles.callSummary} aria-label="Показатели звонка">
        <SummaryValue label="Тип" value={callKindLabel(info.kind)} />
        <SummaryValue label="Длительность" value={formatDurationMs(info.durationMs)} />
        <SummaryValue label="Говорил агент" value={formatDurationMs(info.agentTalkMs)} />
        <SummaryValue label="Говорил клиент" value={formatDurationMs(info.humanTalkMs)} />
        <SummaryValue label="Реплик" value={String(info.turnsCount)} />
        <SummaryValue label="Перебиваний" value={String(info.bargeInCount)} />
        <SummaryValue
          label="Синтез речи"
          value={
            <ElevenLabsCost
              credits={costs.ttsCredits}
              usd={ttsUsd}
              rate={costs.elevenLabsUsdPer1kCredits}
              sourceComplete={ttsSourceComplete}
            />
          }
        />
        <SummaryValue
          label="Диалоговая модель"
          value={formatCallCostUsd(costs.llmUsd, costs.completeness.llmUsd)}
        />
        <SummaryValue
          label="Стоимость"
          value={formatCallCostUsd(costs.totalUsd, costs.completeness.totalUsd, true)}
        />
      </section>

      <CallBilling
        callId={info.id}
        billedMs={info.billedMs}
        note={info.billingNote}
        errors={info.billingErrors ?? 0}
      />

      {info.error && (
        <section className={styles.panel} role="alert">
          <h2 className={styles.panelTitle}>Ошибка звонка</h2>
          <p className={styles.errorText}>{info.error}</p>
        </section>
      )}

      {info.report && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2 className={styles.panelTitle}>Итог разговора</h2>
              <p className={styles.panelHint}>{info.report.outcomeLabel}</p>
            </div>
            <span className={`${styles.status} ${info.report.success ? styles.statusGood : styles.statusWarn}`}>
              Оценка {info.report.score}
            </span>
          </div>
          <p>{info.report.summary || 'Сводка не сформирована.'}</p>
        </section>
      )}

      {info.hasRecording && (
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Запись</h2>
          <audio className={styles.audio} controls preload="metadata" src={adminApi.callAudioUrl(info.id)}>
            Браузер не умеет воспроизводить запись звонка.
          </audio>
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Стенограмма</h2>
            <p className={styles.panelHint}>Только чтение</p>
          </div>
          <span className={styles.muted}>{data.turns.length} реплик</span>
        </div>

        {data.turns.length === 0 ? (
          <p className={styles.muted}>В звонке нет распознанных реплик.</p>
        ) : (
          <div className={styles.transcript}>
            {data.turns.map((turn) => {
              const turnHighlights = highlightsByTurn.get(turn.index) ?? []
              return (
                <article
                  key={turn.index}
                  className={`${styles.turn} ${turn.role === 'agent' ? styles.turnAgent : ''}`}
                >
                  <div className={styles.turnMeta}>
                    <strong>{turn.role === 'agent' ? 'Агент' : 'Собеседник'}</strong>
                    <span>{offsetLabel(turn.offsetMs)}</span>
                  </div>
                  <p className={styles.turnText}>
                    <span className={styles.heard}>{turn.heard || turn.text}</span>
                    {turn.tail && <span className={styles.tail}> {turn.tail}</span>}
                  </p>
                  {turnHighlights.length > 0 && (
                    <div className={styles.highlights}>
                      {turnHighlights.map((highlight, index) => (
                        <span
                          key={`${highlight.kind}-${index}`}
                          className={`${styles.highlight} ${highlight.good ? styles.highlightGood : ''}`}
                          title={highlight.note}
                        >
                          {highlight.label}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        )}
      </section>
    </>
  )
}

function SummaryValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.metric}>
      <span className={styles.metricLabel}>{label}</span>
      <strong className={styles.metricValue}>{value}</strong>
    </div>
  )
}

function offsetLabel(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/** Списание с пакета минут и возврат. */
function CallBilling({
  callId,
  billedMs,
  note,
  errors,
}: {
  callId: number
  billedMs: number | null | undefined
  note: string | undefined
  errors: number
}) {
  const refund = useAdminRefundCall(callId)
  const [reason, setReason] = useState('')
  if (billedMs === null || billedMs === undefined) return null

  function submit() {
    const text = reason.trim()
    if (!text) return
    if (!window.confirm(`Вернуть клиенту ${formatDurationMs(billedMs ?? 0)} за звонок № ${callId}?`)) return
    refund.mutate(text, { onSuccess: () => setReason('') })
  }

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h2 className={styles.panelTitle}>Пакет минут</h2>
          <p className={styles.panelHint}>
            Списано: <strong>{billedMs > 0 ? formatDurationMs(billedMs) : 'ничего'}</strong>
            {note ? ` — ${note}` : ''}
          </p>
          {errors > 0 && (
            <p className={styles.panelHint}>Сбоев синтеза или модели за звонок: {errors}</p>
          )}
        </div>
      </div>
      {billedMs > 0 && (
        <div className={styles.refundBox}>
          <label className={`${styles.field} ${styles.fieldGrow}`}>
            <span className={styles.label}>Причина возврата (увидит клиент)</span>
            <input
              className={styles.input}
              maxLength={300}
              placeholder="Например: обрыв линии у оператора"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <Button
            size="sm"
            variant="secondary"
            onClick={submit}
            loading={refund.isPending}
            disabled={!reason.trim()}
          >
            Вернуть минуты
          </Button>
        </div>
      )}
    </section>
  )
}
